import React from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import { Rocket, Home as HomeIcon, Store, BookOpen, LogOut, Trophy, Film, Inbox, Sparkles } from "lucide-react";
import { useAuth } from "../lib/AuthContext";

const NavItem = ({ to, icon: Icon, label, active, testId }) => (
  <Link
    to={to}
    data-testid={testId}
    className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
      active
        ? "bg-black text-white"
        : "text-gray-700 hover:bg-gray-100"
    }`}
  >
    <Icon size={16} />
    <span>{label}</span>
  </Link>
);

export default function Layout() {
  const { pathname } = useLocation();
  const { user, logout } = useAuth();

  return (
    <div className="min-h-screen flex bg-[#F4F6F8]">
      {/* Sidebar - mimics Shopify admin nav */}
      <aside className="hidden md:flex w-64 flex-col bg-white border-r border-gray-200 fixed inset-y-0 left-0 z-20">
        <div className="px-6 py-5 border-b border-gray-100 flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-black text-white flex items-center justify-center">
            <Rocket size={16} />
          </div>
          <div>
            <div className="text-sm font-semibold tracking-tight text-gray-900">Launchpad</div>
            <div className="text-[10px] uppercase tracking-widest text-gray-500">Shopify App</div>
          </div>
        </div>
        <nav className="p-4 space-y-1 flex-1" data-testid="primary-nav">
          <NavItem
            to="/app"
            icon={HomeIcon}
            label="Dashboard"
            active={pathname === "/app"}
            testId="nav-dashboard"
          />
          <NavItem
            to="/app/campaigns/new"
            icon={Rocket}
            label="New campaign"
            active={pathname === "/app/campaigns/new"}
            testId="nav-new-campaign"
          />
          <NavItem
            to="/app/rounds"
            icon={Trophy}
            label="Launch Rounds"
            active={pathname.startsWith("/app/rounds")}
            testId="nav-rounds"
          />
          <NavItem
            to="/app/reels"
            icon={Film}
            label="Reels"
            active={pathname.startsWith("/app/reels")}
            testId="nav-reels"
          />
          <NavItem
            to="/app/applications"
            icon={Inbox}
            label="Cut Applications"
            active={pathname.startsWith("/app/applications")}
            testId="nav-applications"
          />
          <NavItem
            to="/app/the-cut"
            icon={Sparkles}
            label="The Cut"
            active={pathname.startsWith("/app/the-cut")}
            testId="nav-the-cut"
          />
          <div className="pt-4 mt-4 border-t border-gray-100">
            <div className="lp-section-label px-3 mb-2">Preview</div>
            <NavItem
              to="/storefront"
              icon={Store}
              label="Storefront preview"
              active={pathname.startsWith("/storefront")}
              testId="nav-storefront"
            />
          </div>
        </nav>
        <div className="p-4 border-t border-gray-100 space-y-3">
          {user && user.email && (
            <div className="flex items-center justify-between gap-2 px-1" data-testid="user-bar">
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-widest text-gray-400">Signed in</div>
                <div className="text-xs font-medium text-gray-900 truncate" data-testid="user-email">
                  {user.email}
                </div>
              </div>
              <button
                onClick={logout}
                className="lp-btn-secondary text-xs shrink-0"
                data-testid="logout-btn"
                title="Sign out"
              >
                <LogOut size={12} /> Sign out
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 md:ml-64 min-h-screen">
        <div className="max-w-7xl mx-auto p-6 md:p-8 lp-fade-in">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
